import * as React from 'react';
import { useState, useEffect } from 'react';
import { FormControl, Button, Grid, FormHelperText, Card, Select, MenuItem, Box, Typography, FormLabel, OutlinedInput, InputAdornment } from '@mui/material';
import { postPatientInformationRequest, putPatientInformationRequest } from '../../../../redux/modules/admin/patientInformation/slice';
import { getPatientDataRequest } from '../../../../redux/modules/admin/patientProfile/slice';
import { putPatientEmergencyContactRequest } from '../../../../redux/modules/admin/patientEmergencyContactInfo/slice';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector, useDispatch } from 'react-redux';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import ReactSelect from "react-select";
import { countries } from "countries-list";
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneField from '../../../../components/PhoneField';
import { GENDER } from '../../../../constants/Constant';

const validationSchema = yup.object().shape({
    fName: yup.string().required('First Name is required').matches(/^[A-Za-z ]*$/, 'Please enter valid name'),
    lName: yup.string().required('Last Name is required').matches(/^[A-Za-z ]*$/, 'Please enter valid name'),
    email: yup.string().email('Invalid email').nullable(),
    gender: yup.string().required('Gender is required'),
    dob: yup.string().required('Date of Birth is required'),
    address: yup.string(),
    region: yup.string(),
    country: yup.string(),
    postalCode: yup.string().nullable(),
    methodOfCommunication: yup.string(),
    contactNumber: yup.string().nullable().test('phone', 'Please enter a valid phone number', (val) => {
        if (!val || val === '') return true;
        return isValidPhoneNumber(val);
    }),
    company: yup.string().nullable(),
    emergencyContactName: yup.string().matches(/^[A-Za-z ]*$/, 'Please enter valid name'),
    relationShipToPatient: yup.string(),
    emergencyContactNumber: yup.string().nullable().test('phone', 'Please enter a valid phone number', (val) => {
        if (!val || val === '') return true;
        return isValidPhoneNumber(val);
    }),
});

const BasicInfo = ({ itemData, formData, onNext, onBack }) => {
    const dispatch = useDispatch();
    const [gender, setGender] = useState('');
    const [country, setCountry] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [communicationMethod, setCommunicationMethod] = useState('');
    const [patientRelation, setPatientRelation] = useState('')
    let { currentBranch } = useSelector(state => state.branch);
    let { getMasterRes, getPatientDataRes } = useSelector(state => state.patientProfile);

    const { register, handleSubmit, formState: { errors }, setValue, getValues, trigger, clearErrors, watch } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {...formData, dob: formData?.dob ? formData.dob : moment().format('YYYY-MM-DD'), contactNumber: formData?.contactNumber || '' },
    });
    // Subscribe to phone fields so PhoneField rerenders in sync with the form
    const contactNumberValue = watch('contactNumber');
    const emergencyContactNumberValue = watch('emergencyContactNumber');

    useEffect(() => {
        const pId = localStorage.getItem("pId")
        if (itemData?._id || pId) {

            dispatch(getPatientDataRequest({ type: "basicInformation", id: itemData?._id !== undefined ? itemData?._id : pId }))
        }


    }, [dispatch, itemData])



    useEffect(() => {
        if (getPatientDataRes?.patientData) {
            setValue('fName', getPatientDataRes?.patientData?.fName);
            setValue('lName', getPatientDataRes?.patientData?.lName);
            setValue('gender', getPatientDataRes?.patientData?.gender);
            setValue('dob', getPatientDataRes?.patientData?.dob);
            setValue('email', getPatientDataRes?.patientData?.email);
            setValue('address', getPatientDataRes?.patientData?.address);
            setValue('postalCode', getPatientDataRes?.patientData?.postalCode);
            // Legacy records may have phone numbers without the leading '+'.
            // react-phone-number-input expects E.164 (e.g. +5926001234), so
            // prepend '+' if the stored value looks like raw digits.
            const toE164 = (p) => {
                if (!p) return '';
                const s = String(p).trim();
                if (s === '') return '';
                return s.startsWith('+') ? s : '+' + s.replace(/\D/g, '');
            };
            setValue('contactNumber', toE164(getPatientDataRes?.patientData?.contactNumber));
            setValue('company', getPatientDataRes?.patientData?.company);
            setValue('emergencyContactName', getPatientDataRes?.patientData?.emergencyContactName);
            setValue('emergencyContactNumber', toE164(getPatientDataRes?.patientData?.emergencyContactNumber));
            setValue('region', getPatientDataRes?.patientData?.region);
            setValue('country', getPatientDataRes?.patientData?.country);
            setValue('methodOfCommunication', getPatientDataRes?.patientData?.methodOfCommunication);
            setValue('relationShipToPatient', getPatientDataRes?.patientData?.relationShipToPatient);
            setGender(getPatientDataRes?.patientData?.gender)
            setCountry(getPatientDataRes?.patientData?.country)
            setCommunicationMethod(getPatientDataRes?.patientData?.methodOfCommunication)
            setPatientRelation(getPatientDataRes?.patientData?.relationShipToPatient)
            const selectedCountry = getPatientDataRes?.patientData?.country
                ? { value: getPatientDataRes?.patientData?.country, label: getPatientDataRes?.patientData?.country }
                : null;
            setSelectedCountry(selectedCountry);

        }
    }, [getPatientDataRes, setValue, setGender, setCountry, setCommunicationMethod, setPatientRelation]);

    const onSubmit = (data) => {
        // react-phone-number-input emits E.164 (e.g. "+5926001234") or
        // empty string when cleared. Normalize empty → null so the server
        // stores it as missing rather than as an empty string.
        if (!data.contactNumber || data.contactNumber.trim() === '') {
            data.contactNumber = null;
        }
        if (!data.emergencyContactNumber || data.emergencyContactNumber.trim() === '') {
            data.emergencyContactNumber = null;
        }

        const pId = localStorage.getItem("pId")
        if (itemData?._id || pId) {
            data.country = country;
            data.methodOfCommunication = communicationMethod
            data.relationShipToPatient = patientRelation
            data.gender = gender
            dispatch(putPatientInformationRequest({ formData: data, _id: itemData?._id !== undefined ? itemData?._id : pId }))
            dispatch(putPatientEmergencyContactRequest({ formData: data, _id: itemData?._id !== undefined ? itemData?._id : pId ,branchId: currentBranch?.id}))
            // PUT path: the record already exists, advance immediately.
            onNext({ basicInfo: data });
        }
        else {
            // POST path: don't advance yet. The wrapping FormStepper watches
            // the response and advances only after the patient is actually
            // created, so a duplicate-email or duplicate-name-and-DOB 409
            // can't silently let the user grind through the rest of the form
            // with no patient on the server.
            dispatch(postPatientInformationRequest({ formData: data, branchId: currentBranch?.id }))
        }
    };
    const nextStep = () => {
        onNext();
    }
    const handleChange = (fieldName) => (event, selected, options) => {
        switch (fieldName) {
            case 'gender':
                setGender(event.target.value);
                setValue('gender', event.target.value);
                clearErrors('gender')
                break;
            case 'methodOfCommunication':
                setCommunicationMethod(event.target.value);
                break;
            case 'relation':
                setPatientRelation(event.target.value);
                break;
            default:

        }
    };
    const handleSearchInputChange = (selected) => {
        if (selected) {
            setCountry(selected?.value)
            setSelectedCountry(selected);
        }
    };
    const options = Object.entries(countries)
        .map((code) => {
            return {
                value: code[1].name,
                label: (
                    <>
                        <span> {code[1].name}</span>
                    </>
                )
            };
        })
        .filter(Boolean);

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Box>
                <Card className='cardStyle' sx={{ marginTop: "40px" }}>
                    <Typography className='title'>Personal Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }} required>First Name</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.fName)} className='inputfield'>
                                <OutlinedInput placeholder='First Name' fullWidth {...register('fName')} />
                                {errors?.fName?.message && <FormHelperText>{errors?.fName?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }} required>Last Name</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.lName)} className='inputfield' >
                                <OutlinedInput placeholder="Last Name" fullWidth {...register('lName')} />
                                {errors?.lName?.message && <FormHelperText>{errors?.lName?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} className='girdMarginBottom'>
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }} required>Gender</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.gender)} className='inputfield'>
                                <Select {...register('gender')} value={gender} onChange={handleChange('gender')}>
                                    <MenuItem value={GENDER.MALE}>Male</MenuItem>
                                    <MenuItem value={GENDER.FEMALE}>Female</MenuItem>
                                    <MenuItem value={GENDER.OTHER}>Other</MenuItem>
                                </Select>
                                {errors?.gender?.message && <FormHelperText>{errors?.gender?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }} required>DOB</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.dob)}>
                                <LocalizationProvider dateAdapter={AdapterMoment}>
                                    <DatePicker format="DD/MM/YYYY" disableFuture={true} className="inputfield" value={moment.utc(getValues().dob)} onChange={(date) => { setValue("dob", moment(date).format('YYYY-MM-DD')); trigger("dob") }} />
                                </LocalizationProvider>
                                {errors?.dob?.message && <FormHelperText>{errors?.dob?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Email</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.email)} className='inputfield'>
                                <OutlinedInput placeholder="Email (optional)" type='email' fullWidth {...register('email')} />
                                {errors?.email?.message && <FormHelperText>{errors?.email?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Card>

                <Card sx={{ marginTop: "50px" }} className='cardStyle'>
                    <Typography className='title'>Contact Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Address</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.address)} className='inputfield'>
                                <OutlinedInput placeholder="Address" fullWidth {...register('address')} />
                                {errors?.address?.message && <FormHelperText>{errors?.address?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Region</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.region)} className='inputfield'>
                                <OutlinedInput placeholder="Region" fullWidth {...register('region')} />
                                {errors?.region?.message && <FormHelperText>{errors?.region?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormControl fullWidth error={Boolean(errors?.referringConsultant)} className='selectDropdown'>
                                <FormLabel sx={{ marginBottom: "9px" }} >Country</FormLabel>
                                <ReactSelect
                                    {...register('country')}
                                    autoFocus={false}
                                    value={selectedCountry}
                                    onChange={handleSearchInputChange}
                                    placeholder="Select a Country"
                                    isMulti={false}
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={false}
                                    options={options}
                                    classNamePrefix="select"
                                />
                                {errors?.referringConsultant?.message && <FormHelperText>{errors?.referringConsultant?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Postal Code</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.postalCode)} className='inputfield'>
                                <OutlinedInput  placeholder="Postal Code" fullWidth {...register('postalCode')} />
                                {errors?.postalCode?.message && <FormHelperText>{errors?.postalCode?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Method of Communication</FormLabel>
                            <FormControl fullWidth error={Boolean(errors.methodOfCommunication)} className='inputfield'>
                                <Select {...register('methodOfCommunication')} value={communicationMethod} onChange={handleChange('methodOfCommunication')}>
                                    <MenuItem value="Video call">Video call</MenuItem>
                                    <MenuItem value="Voice call">Voice call</MenuItem>
                                </Select>
                                {errors?.methodOfCommunication?.message && <FormHelperText>{errors?.methodOfCommunication?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Contact Number</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.contactNumber)}>
                                <PhoneField
                                    value={contactNumberValue}
                                    onChange={(v) => { setValue('contactNumber', v); trigger('contactNumber'); }}
                                    error={Boolean(errors?.contactNumber)}
                                />
                                {errors?.contactNumber?.message && <FormHelperText>{errors?.contactNumber?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Company / Employer</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.company)} className='inputfield'>
                                <OutlinedInput placeholder="Company or employer (optional)" fullWidth {...register('company')} />
                                {errors?.company?.message && <FormHelperText>{errors?.company?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Card>
                <Card sx={{ marginTop: "40px", marginBottom: "40px" }} className='cardStyle'>
                    <Typography className='title'> Emergency Contact Information</Typography>
                    <Grid container spacing={2}>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Name of Emergency Contact</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.emergencyContactName)} className='inputfield'>
                                <OutlinedInput
                                    placeholder="Name of Emergency Contact" fullWidth {...register('emergencyContactName')} />
                                {errors?.emergencyContactName?.message && <FormHelperText>{errors?.emergencyContactName?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Relationship To Patient</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.relationShipToPatient)} className='inputfield'>
                                <Select  {...register('relationShipToPatient')}
                                    value={patientRelation}
                                    onChange={handleChange('relation')}>
                                    {getMasterRes?.relationList?.map((elm) => (
                                        <MenuItem key={elm._id} value={elm.relation}>
                                            {elm.relation}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors?.relationShipToPatient?.message && <FormHelperText>{errors?.relationShipToPatient?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                        <Grid item lg={4} md={6} sm={6} xs={12} >
                            <FormLabel sx={{ fontSize: "20px", color: "primary.dark" }}>Emergency Contact Number</FormLabel>
                            <FormControl fullWidth error={Boolean(errors?.emergencyContactNumber)}>
                                <PhoneField
                                    value={emergencyContactNumberValue}
                                    onChange={(v) => { setValue('emergencyContactNumber', v); trigger('emergencyContactNumber'); }}
                                    error={Boolean(errors?.emergencyContactNumber)}
                                />
                                {errors?.emergencyContactNumber?.message && <FormHelperText>{errors?.emergencyContactNumber?.message}</FormHelperText>}
                            </FormControl>
                        </Grid>
                    </Grid>
                </Card>
            </Box>
            <Box display={'flex'} flexDirection={'row'} alignItems={'left'} justifyContent={'end'} >
                <Button type='submit' className='commonBtn' >
                    {getPatientDataRes?.patientData ? "Update and Next" : "Save and Next"}
                </Button>
                {itemData?._id || localStorage.getItem("pId") ? (
                    <Button className='commonBtn' sx={{ ml: 2 }} onClick={nextStep}>
                        Next
                    </Button>
                ) : null}
            </Box>
        </form >
    );
};

export default BasicInfo;
